import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { inviteUserSchema, type InviteUserInput, type MembershipResponse, type RoleResponse, type TeamResponse } from '@orion/contracts';
import { Button, Card, DataTable, Input, Menu, Pill } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useAuth } from '../../../lib/auth.js';
import { ApiError } from '../../../lib/api-client.js';
import { useRolesQuery } from '../roles/api.js';
import { useTeamsQuery } from '../teams/api.js';
import { useDeleteUser, useInviteUser, useMembershipsQuery, useSetUserStatus, useUpdateMembership } from './api.js';

const STATUS_COLOR: Record<string, string> = { active: '#1E7A4C', invited: '#8A4B06', deactivated: '#5E6470', removed: '#8A909C' };
const SOURCE_LABEL: Record<MembershipResponse['signupSource'], string> = { invited: 'Invited', sso: 'Microsoft SSO' };
const SOURCE_COLOR: Record<MembershipResponse['signupSource'], string> = { invited: '#454A54', sso: '#5B34B0' };
const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'invited', label: 'Invited' },
  { key: 'deactivated', label: 'Deactivated' },
] as const;

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'Now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function MicrosoftLogo() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
      <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
      <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
      <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
    </svg>
  );
}

/** docs/architecture/06-rbac.md — every user (invited by hand, or
 * provisioned automatically on first Entra SSO sign-in with the tenant's
 * default role) needs a way for an admin to change what they can do
 * afterwards, see where they came from, and — separately from deactivate —
 * remove them outright when that's actually safe to do.
 *
 * Status tabs and the Role/Team/Auth-method filters are computed client-
 * side from the one already-fetched page (there's no per-status count
 * endpoint) — consistent with how every other list screen in this app
 * handles counts under a pageSize cap. No bulk-select/bulk-action toolbar
 * and no "choose columns" — the design shows checkboxes and a column
 * picker, but nothing in this codebase implements a bulk user action or
 * configurable columns, and a checkbox with no action behind it is worse
 * than not showing one. No "seats used" meter — this workspace has no
 * seat-limit/billing concept to measure against. */
export function UsersPage() {
  const { session } = useAuth();
  const { data, isLoading } = useMembershipsQuery();
  const { data: roles } = useRolesQuery();
  const { data: teams } = useTeamsQuery();
  const inviteUser = useInviteUser();
  const setStatus = useSetUserStatus();
  const deleteUser = useDeleteUser();
  const [showInvite, setShowInvite] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<(typeof STATUS_TABS)[number]['key']>('all');
  const [q, setQ] = useState('');
  const [roleId, setRoleId] = useState<string | undefined>(undefined);
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const [authMethod, setAuthMethod] = useState<MembershipResponse['signupSource'] | undefined>(undefined);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteUserInput>({ resolver: zodResolver(inviteUserSchema), defaultValues: { roleIds: [], teamIds: [] } });

  const allUsers = data?.data ?? [];
  const counts = {
    all: allUsers.length,
    active: allUsers.filter((u) => u.status === 'active').length,
    invited: allUsers.filter((u) => u.status === 'invited').length,
    deactivated: allUsers.filter((u) => u.status === 'deactivated').length,
  };

  const users = useMemo(() => {
    return allUsers.filter((u) => {
      if (tab !== 'all' && u.status !== tab) return false;
      if (q && !u.displayName.toLowerCase().includes(q.toLowerCase()) && !u.email.toLowerCase().includes(q.toLowerCase())) return false;
      if (roleId && !u.roles.some((r) => r.id === roleId)) return false;
      if (teamId && !u.teams.some((t) => t.id === teamId)) return false;
      if (authMethod && u.signupSource !== authMethod) return false;
      return true;
    });
  }, [allUsers, tab, q, roleId, teamId, authMethod]);

  const salesRole = roles?.find((r) => r.key === 'sales' || r.name.toLowerCase() === 'sales');
  const editingUser = editingId ? allUsers.find((u) => u.id === editingId) : undefined;

  const onSubmit = async (input: InviteUserInput) => {
    await inviteUser.mutateAsync({ ...input, roleIds: input.roleIds.length ? input.roleIds : salesRole ? [salesRole.id] : [] });
    reset();
    setShowInvite(false);
  };

  const confirmDelete = async (id: string, name: string) => {
    setDeleteError(null);
    setDeleteNotice(null);
    try {
      const result = await deleteUser.mutateAsync(id);
      setConfirmDeleteId(null);
      setDeleteNotice(
        result.mode === 'hard'
          ? `${name} was permanently deleted.`
          : `${name} owns records or has activity history, so they were removed from the organisation instead — greyed out below, with their records and history intact.`,
      );
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.problem.title : 'Could not remove this user.');
    }
  };

  return (
    <>
      <PageHeader
        title="Users"
        actions={
          <Button size="sm" onClick={() => setShowInvite((s) => !s)}>
            Invite user
          </Button>
        }
      />
      <main className="flex flex-1 flex-col gap-4 p-7">
        <p className="-mt-2 text-sm text-text-muted">Everyone who can sign in to OES Workspace, their role, team and how they authenticate.</p>

        {showInvite && (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface p-4">
            <Input label="Name" error={errors.displayName?.message} {...register('displayName')} />
            <Input label="Email" type="email" error={errors.email?.message} {...register('email')} />
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Role
              <select className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" {...register('roleIds.0')}>
                {(roles ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit" size="sm" loading={isSubmitting}>
              Send invite
            </Button>
          </form>
        )}

        {editingUser && <EditRolesPanel membership={editingUser} roles={roles ?? []} teams={teams ?? []} onClose={() => setEditingId(null)} />}

        {deleteError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {deleteError}
          </div>
        )}
        {deleteNotice && (
          <div role="status" className="rounded-control bg-success-soft px-3 py-2 text-sm text-success">
            {deleteNotice}
          </div>
        )}

        <div role="tablist" aria-label="User status" className="flex gap-5 border-b border-border">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex h-9 items-center gap-1.5 text-sm ${tab === t.key ? 'border-b-2 border-primary font-semibold text-ink' : 'font-medium text-text-muted hover:text-ink'}`}
            >
              {t.label}
              <span className="flex h-5 items-center rounded-pill bg-neutral-pill-soft px-1.75 text-xs font-semibold text-text-secondary">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <label className="flex h-9 w-70 items-center gap-2 rounded-control border border-border-strong bg-surface px-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <span className="sr-only">Search users</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} type="search" placeholder="Search name or email" className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none" />
          </label>
          <Menu
            trigger={
              <button type="button" className="flex h-9 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-sm text-text-secondary">
                Role: {roleId ? (roles?.find((r) => r.id === roleId)?.name ?? 'All') : 'All'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[{ label: 'All', onSelect: () => setRoleId(undefined) }, ...(roles ?? []).map((r) => ({ label: r.name, onSelect: () => setRoleId(r.id) }))]}
            align="left"
          />
          <Menu
            trigger={
              <button type="button" className="flex h-9 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-sm text-text-secondary">
                Team: {teamId ? (teams?.find((t) => t.id === teamId)?.name ?? 'All') : 'All'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[{ label: 'All', onSelect: () => setTeamId(undefined) }, ...(teams ?? []).map((t) => ({ label: t.name, onSelect: () => setTeamId(t.id) }))]}
            align="left"
          />
          <Menu
            trigger={
              <button type="button" className="flex h-9 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-sm text-text-secondary">
                Auth method: {authMethod ? SOURCE_LABEL[authMethod] : 'All'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            }
            items={[
              { label: 'All', onSelect: () => setAuthMethod(undefined) },
              { label: 'Invited', onSelect: () => setAuthMethod('invited') },
              { label: 'Microsoft SSO', onSelect: () => setAuthMethod('sso') },
            ]}
            align="left"
          />
          <div className="flex-1" />
          <a href="/api/v1/admin/users/export" className="flex h-9 items-center gap-1.5 rounded-control px-3 text-sm font-semibold text-primary-soft-text hover:bg-primary-soft">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v12M7 11l5 5 5-5" />
              <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            Export
          </a>
        </div>

        {!isLoading && users.length > 0 && (
          <DataTable
            rows={users}
            rowKey={(u) => u.id}
            rowClassName={(u) => (u.status === 'removed' ? 'opacity-50 saturate-50' : undefined)}
            columns={[
              {
                key: 'name',
                header: 'User',
                width: '1.8fr',
                render: (u) => (
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[11px] font-semibold text-primary-soft-text">{initialsOf(u.displayName)}</span>
                    <div className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate font-medium text-ink">
                        {u.displayName}
                        {session?.user.id === u.userId && <span className="ml-1 font-medium text-text-muted">(you)</span>}
                      </span>
                      <span className="truncate text-xs text-text-muted">{u.email}</span>
                    </div>
                  </div>
                ),
              },
              { key: 'roles', header: 'Role', width: '1fr', render: (u) => u.roles.map((r) => r.name).join(', ') || '—' },
              { key: 'teams', header: 'Team', width: '1fr', render: (u) => u.teams.map((t) => t.name).join(', ') || '—' },
              {
                key: 'source',
                header: 'Sign-in method',
                width: '1.2fr',
                render: (u) =>
                  u.signupSource === 'sso' ? (
                    <span className="flex items-center gap-1.5 text-[13px] text-text-secondary">
                      <MicrosoftLogo />
                      Microsoft SSO
                    </span>
                  ) : (
                    <Pill label={SOURCE_LABEL[u.signupSource]} color={SOURCE_COLOR[u.signupSource]} />
                  ),
              },
              {
                key: 'status',
                header: 'Status',
                width: '0.9fr',
                render: (u) => <Pill label={u.status === 'removed' ? 'removed — left org' : u.status} color={STATUS_COLOR[u.status]} />,
              },
              { key: 'lastActive', header: 'Last active', width: '0.9fr', render: (u) => <span className="text-[13px] text-text-muted">{timeAgo(u.lastActiveAt)}</span> },
              {
                key: 'actions',
                header: '',
                width: '1.8fr',
                render: (u) =>
                  confirmDeleteId === u.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-text-secondary">{u.deletable ? `Permanently delete ${u.displayName}?` : `Remove ${u.displayName} from the organisation?`}</span>
                      <button type="button" className="text-xs font-semibold text-danger" onClick={() => confirmDelete(u.id, u.displayName)} disabled={deleteUser.isPending}>
                        {deleteUser.isPending ? 'Working…' : 'Confirm'}
                      </button>
                      <button type="button" className="text-xs font-semibold text-text-muted" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button type="button" className="text-xs font-semibold text-primary" onClick={() => setEditingId(u.id)}>
                        Edit roles
                      </button>
                      {u.status === 'active' || u.status === 'invited' ? (
                        <button type="button" className="text-xs font-semibold text-danger" onClick={() => setStatus.mutate({ id: u.id, status: 'deactivate' })}>
                          Deactivate
                        </button>
                      ) : (
                        <button type="button" className="text-xs font-semibold text-primary" onClick={() => setStatus.mutate({ id: u.id, status: 'reactivate' })}>
                          Reactivate
                        </button>
                      )}
                      <button
                        type="button"
                        title={u.deletable ? 'Permanently deletes this account' : `Has ${u.blockingRecordCount} linked record(s) — will be marked "removed" and greyed out instead of deleted, keeping their records and history intact`}
                        className="text-xs font-semibold text-danger"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteNotice(null);
                          setConfirmDeleteId(u.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ),
              },
            ]}
          />
        )}
      </main>
    </>
  );
}

function EditRolesPanel({ membership, roles, teams, onClose }: { membership: MembershipResponse; roles: RoleResponse[]; teams: TeamResponse[]; onClose: () => void }) {
  const updateMembership = useUpdateMembership();
  const [roleIds, setRoleIds] = useState<string[]>(membership.roles.map((r) => r.id));
  const [teamIds, setTeamIds] = useState<string[]>(membership.teams.map((t) => t.id));

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const save = async () => {
    if (roleIds.length === 0) return;
    await updateMembership.mutateAsync({ id: membership.id, input: { roleIds, teamIds } });
    onClose();
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary-soft-text">{initialsOf(membership.displayName)}</span>
          <div className="flex flex-col leading-tight">
            <h3 className="text-[15px] font-semibold text-ink">{membership.displayName}</h3>
            <span className="text-xs text-text-muted">
              {membership.email} · {SOURCE_LABEL[membership.signupSource]}
            </span>
          </div>
        </div>
        <button type="button" onClick={onClose} className="text-xs font-medium text-text-muted hover:text-ink">
          Close
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <span className="mb-2 block text-[13px] font-semibold text-ink">Roles</span>
          {roleIds.length === 0 && <p className="mb-2 text-xs font-medium text-danger">At least one role is required.</p>}
          <div className="flex flex-col gap-1.5">
            {roles.map((r) => (
              <label key={r.id} className={`flex items-center gap-2 rounded-control px-2 py-1.5 text-sm ${roleIds.includes(r.id) ? 'bg-primary-soft text-primary-soft-text' : 'text-text-secondary hover:bg-surface-subtle'}`}>
                <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggle(roleIds, setRoleIds, r.id)} />
                {r.name}
              </label>
            ))}
          </div>
        </div>
        <div>
          <span className="mb-2 block text-[13px] font-semibold text-ink">Teams</span>
          <div className="flex flex-col gap-1.5">
            {teams.length === 0 && <p className="text-xs text-text-muted">No teams yet.</p>}
            {teams.map((t) => (
              <label key={t.id} className={`flex items-center gap-2 rounded-control px-2 py-1.5 text-sm ${teamIds.includes(t.id) ? 'bg-primary-soft text-primary-soft-text' : 'text-text-secondary hover:bg-surface-subtle'}`}>
                <input type="checkbox" checked={teamIds.includes(t.id)} onChange={() => toggle(teamIds, setTeamIds, t.id)} />
                {t.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      <Button size="sm" className="mt-5" onClick={save} loading={updateMembership.isPending} disabled={roleIds.length === 0}>
        Save
      </Button>
    </Card>
  );
}
